import { Module, VuexModule, Mutation, MutationAction, getModule } from 'vuex-module-decorators';
import store from '@/store';

import { IDataSet, DataSet } from '@/models/dataset/DataSet';
import { TableTempDataSetFields } from '@/models/tableDataset/TableTempDataSetFields';
import { ApiIdentifers } from '@/config';
import { DataSetSection } from '@/models/dataset/DataSetSection';
import { TableDataSetActionFieldCount } from '@/models/tableDataset/TableDataSetActionFieldCount';
import { TableDataSetColor } from '@/models/tables/TableDataSetColor';
import { ConfigurationField } from '@/models/configuration/Field';
import { ConfigurationService } from '@/services';


export interface IDataSetStoreState {
    dataSet: IDataSet;
}

@Module({ dynamic: true, store, name: 'dataSetStore' })
class DataSetStoreModule extends VuexModule {
    public cloneLineData: DataSetSection[] = [];
    /** A set of max action counter items, e.g. for material */
    public maxActionCounts: TableDataSetActionFieldCount[] = [];
    /**
     * A set of static temp fields to fill up empty action arrays
     */
    public tempTableItems: TableTempDataSetFields[] = [];

    public useOldDataSet: boolean = false;
    public tableScrollPos: number = 0;
    public tableDataSetColors: TableDataSetColor[] = [];
    public fixedLineDataSetIds: string[] = [];
    public processHeaderItems: ConfigurationField[] = [];
    public showProcess: boolean = false;
    public selectedSection: string = '';
    public selectedSubSection: string = '';
    public selectedSampleSection: string = '';


    /**
     * Set dataSet to a new dataSet object
     */
    @MutationAction({ mutate: ['tempTableItems'] })
    public async FetchTempTableItems() {
        DataSetStore.ClearTempTableItems();
        const data: TableTempDataSetFields[] = [];

        const material = ConfigurationService.GetConfiguration.sections.find((sec) => sec.identifier === ApiIdentifers.material);
        const contacts = ConfigurationService.GetConfiguration.sections.find((sec) => sec.identifier === ApiIdentifers.contacts);
        const samples = ConfigurationService.GetSamplesConfiguration.sections.find((sec) => sec.identifier === ApiIdentifers.sample);

        if (material) {
            data.push(new TableTempDataSetFields(material));
        }

        if (contacts) {
            data.push(new TableTempDataSetFields(contacts));
        }

        if (samples) {
            data.push(new TableTempDataSetFields(samples));
        }

        return { tempTableItems: data };
    }

    @MutationAction({ mutate: ['maxActionCounts'] })
    public async FetchMaxActionCounts(inData: TableDataSetActionFieldCount[]) {
        return { maxActionCounts: inData };
    }

    /**
     * Set data of sections to clone
     * @param inData  The sections to clone
     */
    @MutationAction({ mutate: ['cloneLineData'] })
    public async SetCloneLineData(inData: DataSetSection[]) {
        return { cloneLineData: inData };
    }

    /**
     * Set value of useOldDataSet
     * @param inData  True if try to use old data, else false
     */
    @Mutation
    public SetUseOldDataSet(inData: boolean) {
        this.useOldDataSet = inData;
    }

    /**
     * Add a table state (scroll pos) to states
     * @param inScrollPos In table state
     */
    @Mutation
    public async SetTableScrollPos(inScrollPos: number) {
        this.tableScrollPos = inScrollPos;
    }

    /**
     * Set data of inTableDataSetColors
     * @param inTableDataSetColors  The inTableDataSetColors
     */
    @MutationAction({ mutate: ['tableDataSetColors'] })
    public async SetTableDataSetColors(inTableDataSetColors: TableDataSetColor[]) {
        return { tableDataSetColors: inTableDataSetColors };
    }

    /**
     * Set data of inFixedLineDataSetIds
     * @param inFixedLineDataSetIds  The inFixedLineDataSetIds
     */
    @MutationAction({ mutate: ['fixedLineDataSetIds'] })
    public async SetFixedLineDataSetIds(inFixedLineDataSetIds: string[]) {
        return { fixedLineDataSetIds: inFixedLineDataSetIds };
    }

    /**
     * Set data of processHeaderItems
     * @param inProcessHeaderItems  The processHeaderItems
     */
    @MutationAction({ mutate: ['processHeaderItems'] })
    public async SetProcessHeaderItems(inProcessHeaderItems: ConfigurationField[]) {
        return { processHeaderItems: inProcessHeaderItems };
    }

    /**
     * Set data of showProcess
     * @param inShowProcess  The showProcess value
     */
    @Mutation
    public SetShowProcess(inShowProcess: boolean) {
        this.showProcess = inShowProcess;
    }

    /**
     * Set data of selectedSection
     * @param inIdentifier  Identifier string of the section
     */
    @Mutation
    public SetSelectedSection(inIdentifier: string) {
        this.selectedSection = inIdentifier;
    }

    /**
     * Set data of selectedSubSection
     * @param inIdentifier  Identifier string of the section
     */
    @Mutation
    public SetSelectedSubSection(inIdentifier: string) {
        this.selectedSubSection = inIdentifier;
    }

    /**
     * Set data of selectedSampleSection
     * @param inIdentifier  Identifier string of the section
     */
    @Mutation
    public SetSelectedSampleSection(inIdentifier: string) {
        this.selectedSampleSection = inIdentifier;
    }

    /**
     * Clear the whole dataSet
     */
    @Mutation
    public async ClearTempTableItems() {
        this.tempTableItems = [];
    }

    /**
     * Clear cloneLineData
     */
    @Mutation
    public async ClearCloneLineData() {
        this.cloneLineData.splice(0, this.cloneLineData.length);
    }

    /**
     * Clear tableScrollPos
     */
    @Mutation
    public async ClearTableScrollPos() {
        this.tableScrollPos = 0;
    }

    /**
     * Clear tableDataSetColors
     */
    @Mutation
    public async ClearTableDataSetColors() {
        this.tableDataSetColors = [];
    }

    /**
     * Clear tableScrollPos
     */
    @Mutation
    public async ClearFixedLineDataSetIds() {
        this.fixedLineDataSetIds = [];
    }

    /**
     * Clear processHeaderItems
     */
    @Mutation
    public async ClearProcessHeaderItems() {
        this.processHeaderItems = [];
    }

    /**
     * Getter for dataSets
     */
    get GetTempTableItems() {
        return this.tempTableItems;
    }

    /**
     * Getter for maxActionCounts
     */
    get GetMaxActionCounts() {
        return this.maxActionCounts;
    }

    /**
     * Getter for cloneLineData
     */
    get GetCloneLineData() {
        return this.cloneLineData;
    }

    /**
     * Getter for useOldDataSet
     */
    get GetUseOldDataSet() {
        return this.useOldDataSet;
    }

    /**
     * Getter for tableScrollPos
     */
    get GetTableScrollPos() {
        return this.tableScrollPos;
    }

    /**
     * Getter for tableScrollPos
     */
    get GetTableDataSetColors() {
        return this.tableDataSetColors;
    }

    /**
     * Getter for tableScrollPos
     */
    get GetFixedLineDataSetIds() {
        return this.fixedLineDataSetIds;
    }

    /**
     * Getter for tableScrollPos
     */
    get GetProcessHeaderItems() {
        return this.processHeaderItems;
    }

    /**
     * Getter for showProcess
     */
    get GetShowProcess() {
        return this.showProcess;
    }

    /**
     * Getter for selectedSection
     */
    get GetSelectedSection() {
        return this.selectedSection;
    }

    /**
     * Getter for selectedSubSection
     */
    get GetSelectedSubSection() {
        return this.selectedSubSection;
    }

    /**
     * Getter for selectedSampleSection
     */
    get GetSelectedSampleSection() {
        return this.selectedSampleSection;
    }
}

export const DataSetStore = getModule(DataSetStoreModule);
